import React, { useMemo, useCallback } from "react";
import {
  FormProvider,
  useForm,
  type SubmitHandler,
  type Resolver,
  useWatch,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldRenderer } from "../../../components/form/FieldRenderer";
import { licenseCount, paymentTop, paymentDates, comment } from "./config";
import {
  addMspProductLicenseFormSchema,
  AddMSPProductLicenseFormValues,
} from "./schema";
import { Field, Input } from "../../../components/form/atoms";
import { SECTION_CARD_CLASS } from "../../../constants/mspAndCustomerForm";
import { AddMspProductLicenseFormProps } from "./types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { addLicensesToMSPSubscription, calculateAddLicenseCost } from "../services/mspService";
export default function AddMspProductLicenseForm({
  subscription,
  onProductLicenseAdded,
  onCancel,
}: AddMspProductLicenseFormProps) {
  const dispatch = useAppDispatch();
  const resolver = useMemo(
    () =>
      zodResolver(
        addMspProductLicenseFormSchema
      ) as Resolver<AddMSPProductLicenseFormValues>,
    []
  );
  const selectedMspId = useAppSelector(
    (state) => state.selectedMsp.selectedMspId
  );

  const DEFAULT_VALUES = {
    plan_price_id: undefined,
    no_of_licenses: undefined,
    total_paid_amount: undefined,
    discount: undefined,
    payment_mode: undefined,
    transaction_ref: undefined,
    date_of_payment: undefined,
  };

  const methods = useForm<AddMSPProductLicenseFormValues>({
    resolver,
    mode: "onBlur",
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: DEFAULT_VALUES,
  });

  const {
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const handleLicenseBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const licenses = Number(raw || 0);
    if (!licenses || licenses <= 0) {
      setValue("total_paid_amount", 0);
      return;
    }
    const res = await calculateAddLicenseCost(selectedMspId!, subscription?.id!, licenses);
    const amount = res?.amount;
    if (typeof amount === "number") {
      setValue("total_paid_amount", Number(amount.toFixed(2)));
    }
  }
  
  // Form submission handler
  const onSubmit: SubmitHandler<AddMSPProductLicenseFormValues> = useCallback(
    async (formData) => {
      try {
        const payload = {
          licenses: formData.no_of_licenses || 1,
          date_of_payment: formData.date_of_payment || "",
          amount: formData.total_paid_amount || 0,
          transactionDetails: {
            transaction_ref: formData.transaction_ref || "",
            payment_mode: formData.payment_mode || "",
            comments: formData.comment || "",
          }
        };
        await addLicensesToMSPSubscription(selectedMspId!, subscription?.id!, payload);
        reset();
        onCancel && onCancel();
        onProductLicenseAdded && onProductLicenseAdded();
      } catch (error) {
        console.error("Failed to add MSP product:", error);
      }
    },
    [dispatch, reset]
  );

  // Render functions for organization
  const renderHeader = () => (
    <div className="mb-4 flex items-start justify-between p-2">
      <div>
        <h2 className="text-[26px] font-semibold text-[#4F4F4F]">
          Add More Licenses
        </h2>
      </div>
    </div>
  );

  const renderFormSection = (title: string, config: any[]) => (
    <>
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">{title}</h3>
      <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-1">
        {config.map((f) => {
          // if it's the no_of_licenses field, render manually so we can attach onBlur
          if (f.name === "no_of_licenses") {
            return (
              <Field key={f.name} label={f.label ?? "Number of licenses"} required>
                <Input
                  type="number"
                  inputMode="numeric"
                  autoComplete="off"
                  {...register("no_of_licenses", { valueAsNumber: true })}
                  onBlur={handleLicenseBlur}
                />
              </Field>
            );
          }
          // otherwise use existing FieldRenderer for other fields
          return <FieldRenderer key={f.name} cfg={f} />;
        })}
      </div>
    </>
  );

  const renderPaymentDetails = () => (
    <div className="mt-12 rounded-sm border border-[#E6E8EE] bg-white p-6 shadow-[0_8px_24px_rgba(0,0,0,0.06)]">
      <h3 className="text-[15px] font-bold text-[#2B2B2B]">Payment Details</h3>

      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field
          label="Total amount ($)"
          required
          error={errors.total_paid_amount?.message}          
        >
          <Input
            inputMode="decimal"
            autoComplete="off"
            disabled
            {...register("total_paid_amount")}
          />
        </Field>


        {paymentTop
          .filter(
            (f) => f.name === "payment_mode" || f.name === "transaction_ref"
          )
          .map((f) => (
            <FieldRenderer key={f.name} cfg={f} />
          ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
        {paymentDates.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-4">
        {comment.map((f) => (
          <FieldRenderer key={f.name} cfg={f} />
        ))}
      </div>
    </div>
  );

  return (
    <>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="p-6"
          autoComplete="off"
        >
          {renderHeader()}

          <div className={SECTION_CARD_CLASS}>
            <div>
              {renderFormSection("License count", licenseCount)}
            </div>

            {renderPaymentDetails()}

            <div className="mt-8 flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex min-w-[180px] items-center justify-center rounded-[6px] bg-[#1D58E2] px-8 py-3 text-[15px] font-semibold text-white shadow hover:brightness-110 disabled:opacity-50 transition-all duration-200"
              >
                {isSubmitting ? "CREATING..." : "ADD NOW"}
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </>
  );
}
